import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { BACKEND_URL, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MediaEditor from "../components/editor/MediaEditor";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Wand2, Search, RefreshCcw, PlayCircle, Film, Image as ImageIcon,
  Users, Sparkles, FileVideo2, ExternalLink, Loader2, Upload,
} from "lucide-react";

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

function fmtRel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function StatPill({ icon: Icon, label, value, tone = "text-white" }) {
  return (
    <div className="glass rounded-2xl p-4 min-w-[130px]" data-testid={`studio-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums text-white">
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

export default function AdminStudio() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { kind, limit: 300 };
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/studio/drafts", { params });
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || {});
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) { toast.error("Admin only"); nav("/"); return; }
      if (code === 401) { toast.error("Log in first"); nav("/auth?mode=login"); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  useEffect(() => {
    api.get("/admin/studio/analytics?days=30")
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }, []);

  const openStudioPreview = (file) => setPreviewFile(file);
  const closePreview = () => setPreviewFile(null);

  // Admin can open ANY draft's source file in the editor (read-only-ish; changes here
  // still export a new file locally, they don't push to the creator's portfolio)
  const inspectDraft = async (draft) => {
    if (!draft.source_url || draft.source_url.startsWith("local://")) {
      toast.error("This draft has no persisted source file");
      return;
    }
    setOpening(draft.id);
    try {
      const resp = await fetch(toAbsolute(draft.source_url));
      if (!resp.ok) throw new Error(`Fetch failed ${resp.status}`);
      const blob = await resp.blob();
      const type = blob.type || (draft.kind === "video" ? "video/mp4" : "image/jpeg");
      const ext = type.includes("mp4") ? "mp4" : type.includes("webm") ? "webm" : type.includes("png") ? "png" : (draft.kind === "video" ? "mp4" : "jpg");
      const name = draft.title || `${draft.kind}.${ext}`;
      openStudioPreview(new File([blob], name, { type }));
    } catch (e) {
      toast.error("Couldn't load draft source");
    } finally { setOpening(null); }
  };

  const onUpload = (file) => {
    if (!file) return;
    openStudioPreview(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const shown = useMemo(() => rows.length, [rows]);
  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  return (
    <BrandShell title="Admin · Studio">
      <SEO title="Admin · Studio · Nexus" description="Every draft on the platform + try the editor." path="/admin/studio" />
      {previewFile && (
        <MediaEditor file={previewFile} onCancel={closePreview} onExport={closePreview} />
      )}
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Wand2 className="h-3.5 w-3.5 text-nexus-pink" /> Admin only
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Studio</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Every creator draft in one place. Open the editor with any file to try it out.</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="admin-studio-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              className="nexus-gradient text-white border-0 rounded-full h-10 px-4 font-semibold"
              data-testid="admin-studio-try-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Try the editor
            </Button>
            <Button
              onClick={load}
              variant="outline"
              className="rounded-full border-white/10 text-white hover:bg-white/5 h-10"
              data-testid="admin-studio-refresh-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3 flex-wrap" data-testid="admin-studio-stats">
          <StatPill icon={Sparkles} label="Total" value={totals.total} tone="text-nexus-pink" />
          <StatPill icon={FileVideo2} label="Videos" value={totals.videos} tone="text-nexus-purple" />
          <StatPill icon={ImageIcon} label="Images" value={totals.images} tone="text-nexus-blue" />
          <StatPill icon={Users} label="Creators" value={totals.creators} tone="text-emerald-300" />
        </div>

        {/* Analytics — top filters / transitions / templates */}
        {analytics && (
          <div className="glass rounded-2xl p-5" data-testid="admin-studio-analytics">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-nexus-pink" /> Studio analytics · Last 30 days</div>
                <div className="mt-1 text-sm text-zinc-300">
                  <span className="tabular-nums text-white font-semibold">{analytics.totals?.events || 0}</span> events ·
                  <span className="tabular-nums text-white font-semibold ml-1">{analytics.totals?.exports || 0}</span> exports ·
                  <span className="tabular-nums text-white font-semibold ml-1">{analytics.totals?.distinct_creators || 0}</span> creators
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: "Top filters", items: analytics.top_filters, tone: "text-nexus-pink" },
                { title: "Top transitions", items: analytics.top_transitions, tone: "text-nexus-purple" },
                { title: "Top templates", items: analytics.top_templates, tone: "text-nexus-blue" },
              ].map((col) => (
                <div key={col.title}>
                  <div className={`text-[10px] uppercase tracking-widest ${col.tone} mb-2`}>{col.title}</div>
                  {col.items.length === 0 ? (
                    <div className="text-[11px] text-zinc-500 italic">No data yet</div>
                  ) : (
                    <div className="space-y-1.5">
                      {col.items.slice(0, 6).map((it) => (
                        <div key={String(it.value)} className="flex items-center justify-between text-[12px]">
                          <span className="text-white truncate">{it.value}</span>
                          <span className="tabular-nums text-zinc-400">{it.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass rounded-2xl p-4 flex items-center gap-3 flex-wrap" data-testid="admin-studio-filters">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search owner, email or draft title…"
                className="pl-9 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                data-testid="admin-studio-search-input"
              />
            </div>
          </form>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[160px] bg-white/[0.02] border-white/10 text-white" data-testid="admin-studio-kind-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="video">Videos only</SelectItem>
              <SelectItem value="image">Images only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-studio-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : shown === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="admin-studio-empty">
            <Sparkles className="h-6 w-6 mx-auto mb-3 text-nexus-pink" />
            <div className="font-display text-2xl mb-2">No drafts yet</div>
            <p className="text-zinc-500">Tap “Try the editor” above to preview the Studio experience yourself.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-studio-grid">
            {rows.map((d) => {
              const abs = toAbsolute(d.thumb_url || d.source_url);
              const video = d.kind === "video";
              const isLocal = (d.source_url || "").startsWith("local://");
              const isOpening = opening === d.id;
              return (
                <div
                  key={d.id}
                  className="relative aspect-square rounded-xl overflow-hidden hairline group bg-black"
                  data-testid={`admin-studio-draft-${d.id}`}
                >
                  {isLocal ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-2 bg-white/[0.02]">
                      <ImageIcon className="h-6 w-6" />
                      <span className="text-[10px] uppercase tracking-widest">No source</span>
                    </div>
                  ) : video ? (
                    <video src={abs} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  ) : (
                    <img src={abs} alt="" className="w-full h-full object-cover" />
                  )}

                  <div className="absolute top-2 left-2 flex gap-1.5">
                    <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1">
                      {video ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                      {video ? "Video" : "Image"}
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                    <div className="text-[11px] font-semibold truncate text-white">{d.title || "Untitled draft"}</div>
                    <div className="text-[10px] text-zinc-400 truncate">
                      {d.owner?.name || "—"} · {d.owner?.email || ""}
                    </div>
                    <div className="text-[9px] text-zinc-500 uppercase tracking-widest">{fmtRel(d.updated_at)}</div>
                  </div>

                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <Button
                      onClick={() => inspectDraft(d)}
                      disabled={isOpening || isLocal}
                      className="nexus-gradient text-white border-0 rounded-full h-9 px-4 font-semibold disabled:opacity-50"
                      data-testid={`admin-studio-open-${d.id}`}
                    >
                      {isOpening
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Loading…</>
                        : <><PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Open in Studio</>}
                    </Button>
                    {!isLocal && (
                      <a
                        href={toAbsolute(d.source_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white flex items-center gap-1"
                        data-testid={`admin-studio-source-${d.id}`}
                      >
                        <ExternalLink className="h-3 w-3" /> Source
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BrandShell>
  );
}
